import React, { useEffect, useRef, useState, useCallback } from 'react';

/* ─────────────────────────────────────────────────────────────
   IntroAnimation
   Full-screen cinematic intro that plays once per session.
   - Canvas particle network background
   - Animated SVG infinity-like logo ring
   - Typewriter brand-name reveal
   - Shimmer progress bar
   - Split-panel slide-out exit → reveals the page beneath
───────────────────────────────────────────────────────────── */

const LOGO_COLORS = {
  navy: '#1a2f6e',
  green: '#4caf50',
  teal: '#0d9488',
  orange: '#f97316',
};

/* ── Particle canvas ────────────────────────────────────────── */
const ParticleCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let animId;
    const particles = [];
    const COUNT = 24;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Build particles
    for (let i = 0; i < COUNT; i++) {
      const colors = [LOGO_COLORS.green, LOGO_COLORS.teal, LOGO_COLORS.navy, LOGO_COLORS.orange];
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 2 + 1,
        color: colors[Math.floor(Math.random() * colors.length)],
        opacity: Math.random() * 0.4 + 0.2,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw lightweight ambient particles
      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color + Math.round(p.opacity * 255).toString(16).padStart(2, '0');
        ctx.fill();

        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
      });

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        opacity: 0.6,
      }}
    />
  );
};

/* ── Animated SVG logo ring ─────────────────────────────────── */
const LogoRing = ({ visible }) => (
  <div
    style={{
      position: 'relative',
      width: 170,
      height: 170,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: visible ? 1 : 0,
      transform: visible ? 'scale(1)' : 'scale(0.4)',
      transition: 'opacity 0.7s cubic-bezier(0.34,1.56,0.64,1), transform 0.7s cubic-bezier(0.34,1.56,0.64,1)',
    }}
  >
    {/* Outer spinning dashed ring */}
    <svg
      width={170}
      height={170}
      viewBox="0 0 170 170"
      style={{ position: 'absolute', animation: 'sipl-intro-spin 8s linear infinite' }}
    >
      <circle cx={85} cy={85} r={78} fill="none" stroke={LOGO_COLORS.green}
        strokeWidth={2} strokeDasharray="12 8" opacity={0.7} />
    </svg>

    {/* Inner spinning ring (counter) */}
    <svg
      width={140}
      height={140}
      viewBox="0 0 140 140"
      style={{ position: 'absolute', animation: 'sipl-intro-spin-rev 6s linear infinite' }}
    >
      <circle cx={70} cy={70} r={62} fill="none" stroke={LOGO_COLORS.teal}
        strokeWidth={1.5} strokeDasharray="6 12" opacity={0.5} />
    </svg>

    {/* Gradient progress arc */}
    <svg
      width={170}
      height={170}
      viewBox="0 0 170 170"
      style={{ position: 'absolute', animation: 'sipl-intro-arc-grow 2.5s ease-out forwards' }}
    >
      <defs>
        <linearGradient id="arcGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={LOGO_COLORS.navy} />
          <stop offset="40%" stopColor={LOGO_COLORS.teal} />
          <stop offset="80%" stopColor={LOGO_COLORS.green} />
          <stop offset="100%" stopColor={LOGO_COLORS.orange} />
        </linearGradient>
      </defs>
      <circle
        cx={85} cy={85} r={78}
        fill="none"
        stroke="url(#arcGrad)"
        strokeWidth={5}
        strokeLinecap="round"
        strokeDasharray={`${2 * Math.PI * 78}`}
        strokeDashoffset={`${2 * Math.PI * 78}`}
        transform="rotate(-90 85 85)"
        style={{ animation: 'sipl-arc-dash 2.5s ease-out 0.3s forwards' }}
      />
    </svg>

    {/* Center logo image */}
    <div
      style={{
        width: 100,
        height: 100,
        borderRadius: '50%',
        background: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 8px 32px rgba(26,47,110,0.2)',
        overflow: 'hidden',
        zIndex: 2,
      }}
    >
      <img
        src="/sipl logo.jpeg"
        alt="Seikural Logo"
        decoding="async"
        style={{ width: '85%', height: '85%', objectFit: 'contain' }}
      />
    </div>

    {/* 4 orbit dots */}
    {[0, 90, 180, 270].map((deg, i) => {
      const colors = [LOGO_COLORS.navy, LOGO_COLORS.green, LOGO_COLORS.teal, LOGO_COLORS.orange];
      const rad = (deg * Math.PI) / 180;
      const cx = 85 + 78 * Math.cos(rad) - 6;
      const cy = 85 + 78 * Math.sin(rad) - 6;
      return (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: cx,
            top: cy,
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: colors[i],
            boxShadow: `0 0 12px ${colors[i]}80`,
            animation: `sipl-dot-pulse 2s ease-in-out ${i * 0.5}s infinite`,
          }}
        />
      );
    })}
  </div>
);

/* ── Typewriter text ────────────────────────────────────────── */
const Typewriter = ({ text, speed = 80, delay = 0, color = '#fff' }) => {
  const [displayed, setDisplayed] = useState('');
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  useEffect(() => {
    if (!started) return;
    if (displayed.length >= text.length) return;
    const t = setTimeout(() => {
      setDisplayed(text.slice(0, displayed.length + 1));
    }, speed);
    return () => clearTimeout(t);
  }, [displayed, started, text, speed]);

  return (
    <span style={{ color, fontFamily: 'inherit' }}>
      {displayed}
      <span
        style={{
          display: 'inline-block',
          width: 2,
          height: '1em',
          background: color,
          marginLeft: 3,
          verticalAlign: 'middle',
          animation: 'sipl-cursor-blink 0.8s step-end infinite',
          opacity: displayed.length < text.length ? 1 : 0,
        }}
      />
    </span>
  );
};

/* ── Progress bar ───────────────────────────────────────────── */
const ProgressBar = ({ progress }) => (
  <div
    style={{
      width: '100%',
      maxWidth: 300,
      height: 3,
      background: 'rgba(255,255,255,0.12)',
      borderRadius: 99,
      overflow: 'hidden',
      marginTop: 24,
    }}
  >
    <div
      style={{
        height: '100%',
        width: `${progress}%`,
        background: `linear-gradient(90deg, ${LOGO_COLORS.navy}, ${LOGO_COLORS.teal}, ${LOGO_COLORS.green})`,
        borderRadius: 99,
        transition: 'width 0.15s ease',
        boxShadow: `0 0 12px ${LOGO_COLORS.green}80`,
      }}
    />
  </div>
);

/* ── Split-panel exit ───────────────────────────────────────── */
const SplitPanel = ({ exiting, side }) => (
  <div
    style={{
      position: 'absolute',
      top: 0,
      [side]: 0,
      width: '50%',
      height: '100%',
      background: 'linear-gradient(135deg, #0a0e1a 0%, #0f1e3d 60%, #0d1f2d 100%)',
      transform: exiting
        ? `translateX(${side === 'left' ? '-102%' : '102%'})`
        : 'translateX(0)',
      transition: 'transform 0.85s cubic-bezier(0.77, 0, 0.175, 1)',
      zIndex: 2,
    }}
  />
);

/* ── Main component ─────────────────────────────────────────── */
const IntroAnimation = ({ onComplete }) => {
  const [phase, setPhase] = useState('enter');   // enter | active | exit | done
  const [progress, setProgress] = useState(0);
  const [logoVisible, setLogoVisible] = useState(false);
  const [textReady, setTextReady] = useState(false);
  const completeRef = useRef(false);

  const finish = useCallback(() => {
    if (completeRef.current) return;
    completeRef.current = true;
    setPhase('exit');
    setTimeout(() => {
      setPhase('done');
      if (onComplete) onComplete();
    }, 500);
  }, [onComplete]);

  useEffect(() => {
    // Show logo quickly
    const t1 = setTimeout(() => setLogoVisible(true), 120);
    // Show text quickly
    const t2 = setTimeout(() => setTextReady(true), 320);

    // Animate progress bar 0→100 smoothly over 1.1s
    let pct = 0;
    const interval = setInterval(() => {
      pct += 4;
      setProgress(Math.min(pct, 100));
      if (pct >= 100) clearInterval(interval);
    }, 35);

    // Trigger smooth exit after 1.4s total
    const t3 = setTimeout(finish, 1400);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearInterval(interval);
    };
  }, [finish]);

  if (phase === 'done') return null;

  return (
    <>
      {/* Keyframes injected inline */}
      <style>{`
        @keyframes sipl-intro-spin       { from{transform:rotate(0deg)}   to{transform:rotate(360deg)} }
        @keyframes sipl-intro-spin-rev   { from{transform:rotate(0deg)}   to{transform:rotate(-360deg)} }
        @keyframes sipl-arc-dash {
          from { stroke-dashoffset: ${2 * Math.PI * 78}px; }
          to   { stroke-dashoffset: 0px; }
        }
        @keyframes sipl-dot-pulse {
          0%,100% { transform:scale(1); opacity:1; }
          50%     { transform:scale(1.5); opacity:0.5; }
        }
        @keyframes sipl-cursor-blink {
          0%,100% { opacity:1; }
          50%     { opacity:0; }
        }
        @keyframes sipl-intro-float {
          0%,100% { transform:translateY(0px) rotate(0deg); }
          50%     { transform:translateY(-14px) rotate(180deg); }
        }
        @keyframes sipl-grid-line-h {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
        @keyframes sipl-intro-fade-up {
          from { opacity:0; transform:translateY(20px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes sipl-shimmer-bar {
          0%   { background-position: -300px 0; }
          100% { background-position: 300px 0; }
        }
        @keyframes sipl-glow-pulse {
          0%,100% { opacity:0.4; transform:scale(1); }
          50%     { opacity:0.8; transform:scale(1.2); }
        }
      `}</style>

      <div
        onClick={finish}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          display: 'flex',
          alignItems: 'stretch',
          pointerEvents: phase === 'exit' ? 'none' : 'all',
          opacity: phase === 'exit' ? 0 : 1,
          transition: phase === 'exit' ? 'opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1)' : 'none',
          cursor: 'pointer',
        }}
      >
        {/* Instant Skip Button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            finish();
          }}
          aria-label="Skip introductory animation"
          style={{
            position: 'absolute',
            top: 24,
            right: 28,
            zIndex: 999,
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: 24,
            padding: '7px 16px',
            color: '#ffffff',
            fontSize: '0.8rem',
            fontWeight: 600,
            letterSpacing: '0.06em',
            cursor: 'pointer',
            backdropFilter: 'blur(8px)',
            transition: 'background 0.2s ease',
          }}
        >
          Skip Intro →
        </button>

        {/* Left panel */}
        <SplitPanel exiting={phase === 'exit'} side="left" />
        {/* Right panel */}
        <SplitPanel exiting={phase === 'exit'} side="right" />

        {/* Dark background */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, #060b18 0%, #0a1628 40%, #06101e 100%)',
            zIndex: 0,
          }}
        />

        {/* Glowing radial center blob */}
        <div style={{
          position: 'absolute',
          top: '50%', left: '50%',
          width: 500, height: 500,
          transform: 'translate(-50%,-50%)',
          borderRadius: '50%',
          background: `radial-gradient(circle, rgba(76,175,80,0.12) 0%, rgba(13,148,136,0.07) 40%, transparent 70%)`,
          animation: 'sipl-glow-pulse 4s ease-in-out infinite',
          zIndex: 1,
        }} />

        {/* Particle canvas */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
          <ParticleCanvas />
        </div>

        {/* Decorative grid lines */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 1, overflow: 'hidden', opacity: 0.08 }}>
          {[15, 30, 45, 60, 75].map((pct) => (
            <div key={pct} style={{
              position: 'absolute',
              top: `${pct}%`, left: 0, right: 0,
              height: 1,
              background: `linear-gradient(90deg, transparent, ${LOGO_COLORS.green}, transparent)`,
              transformOrigin: 'left',
              animation: `sipl-grid-line-h 2s ease-out ${pct * 0.02}s both`,
            }} />
          ))}
        </div>

        {/* Floating decorative orbs */}
        {[
          { top: '8%', left: '6%', size: 60, color: LOGO_COLORS.green, delay: '0s', dur: '7s' },
          { top: '10%', right: '8%', size: 45, color: LOGO_COLORS.teal, delay: '1s', dur: '9s' },
          { top: '75%', left: '4%', size: 35, color: LOGO_COLORS.orange, delay: '2s', dur: '6s' },
          { top: '80%', right: '6%', size: 50, color: LOGO_COLORS.navy, delay: '0.5s', dur: '8s' },
        ].map((orb, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              top: orb.top,
              left: orb.left || 'auto',
              right: orb.right || 'auto',
              width: orb.size,
              height: orb.size,
              borderRadius: '50%',
              border: `1.5px solid ${orb.color}60`,
              boxShadow: `0 0 20px ${orb.color}30`,
              animation: `sipl-intro-float ${orb.dur} ease-in-out ${orb.delay} infinite`,
              zIndex: 2,
            }}
          />
        ))}

        {/* ── Main center content ── */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            gap: 0,
          }}
        >
          {/* Logo ring */}
          <LogoRing visible={logoVisible} />

          {/* Company name */}
          <div
            style={{
              marginTop: 28,
              textAlign: 'center',
              animation: textReady ? 'sipl-intro-fade-up 0.7s ease both' : 'none',
              opacity: textReady ? 1 : 0,
            }}
          >
            <div
              style={{
                fontSize: 'clamp(1.6rem, 4vw, 2.4rem)',
                fontWeight: 800,
                letterSpacing: '0.12em',
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                lineHeight: 1.1,
              }}
            >
              {/* SEI in navy, KURAL in green */}
              <span style={{ color: LOGO_COLORS.navy }}>SEI </span>
              <span
                style={{
                  background: `linear-gradient(135deg, ${LOGO_COLORS.green}, ${LOGO_COLORS.teal})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                KURAL
              </span>
            </div>

            <div
              style={{
                marginTop: 4,
                fontSize: 'clamp(0.7rem, 1.5vw, 0.85rem)',
                letterSpacing: '0.35em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.45)',
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                fontWeight: 600,
              }}
            >
              INNOVATIONS
            </div>
          </div>

          {/* Tagline typewriter */}
          <div
            style={{
              marginTop: 22,
              fontSize: 'clamp(0.8rem, 1.8vw, 1rem)',
              color: 'rgba(255,255,255,0.65)',
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontWeight: 400,
              letterSpacing: '0.04em',
              minHeight: '1.5em',
              textAlign: 'center',
              padding: '0 1rem',
            }}
          >
            {textReady && (
              <Typewriter
                text="Building Digital Solutions for Businesses That Want to Grow."
                speed={42}
                delay={200}
                color="rgba(255,255,255,0.65)"
              />
            )}
          </div>

          {/* Progress bar */}
          <ProgressBar progress={progress} />

          {/* Loading label */}
          <div
            style={{
              marginTop: 10,
              fontSize: '0.7rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.3)',
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            }}
          >
            {progress < 100 ? 'Initializing...' : 'Welcome'}
          </div>
        </div>

        {/* Corner decorations */}
        {[
          { top: 24, left: 24, rotate: 0 },
          { top: 24, right: 24, rotate: 90 },
          { bottom: 24, left: 24, rotate: 270 },
          { bottom: 24, right: 24, rotate: 180 },
        ].map((pos, i) => (
          <svg
            key={i}
            width={36} height={36}
            viewBox="0 0 36 36"
            style={{
              position: 'absolute',
              ...pos,
              transform: `rotate(${pos.rotate}deg)`,
              opacity: 0.4,
              zIndex: 10,
            }}
          >
            <path d="M2 34 L2 2 L34 2" fill="none" stroke={LOGO_COLORS.green} strokeWidth={2} strokeLinecap="round" />
          </svg>
        ))}
      </div>
    </>
  );
};

export default IntroAnimation;
